import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { registerUser, verifyEmail, resendVerification } from '../../services/api';
import { setAuthToken, isAuthenticated, setUser } from '../../utils/auth';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from "@/components/ui/checkbox";
import { API_BASE_URL } from '../../services/api';
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/components/ui/use-toast";
import { Toast } from "@/components/ui/toast";

const CAPTCHA_ENABLED = process.env.REACT_APP_CAPTCHA_ENABLED === 'true';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('register');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();
  const { refreshOrganization } = useContext(OrganizationContext);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuthAndPlan = async () => {
      if (isAuthenticated()) {
        navigate('/dashboard');
        return;
      }

      // Check for selected plan in localStorage
      const selectedPlan = localStorage.getItem('selectedPlan');
      if (!selectedPlan) {
        toast({
          title: "Plan Selection Required",
          description: "Please select a pricing plan before registering.",
          variant: "warning",
          duration: 3000
        });
        
        // Delay navigation to allow toast to be visible
        await new Promise(resolve => setTimeout(resolve, 2000));
        navigate('/pricing');
      }
    };

    checkAuthAndPlan();
  }, [navigate, toast]);

  const validatePassword = (password) => {
    // Note: No need to escape the backslash in template literals
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const isValid = regex.test(password);
    console.log('Password validation result:', isValid); // For debugging
    return isValid;
  };

  const handleReCaptchaVerify = async () => {
    if (!executeRecaptcha) {
      console.warn('Execute recaptcha not yet available');
      return 'disabled';
    }
    
    if (!CAPTCHA_ENABLED) {
      return 'disabled';
    }

    try {
      const token = await executeRecaptcha('register');
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      throw new Error('Failed to verify reCAPTCHA');
    }
  };

  const getPasswordValidationError = (password) => {
    if (!password || password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[@$!%*?&]/.test(password)) {
      return 'Password must contain at least one special character (@$!%*?&)';
    }
    return '';
  };

  // In your handleRegister function:
  const handleRegister = async (e) => {
    e.preventDefault();
  
    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character');
      return;
    }
    
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and AI Usage Policy to register');
      return;
    }
  
    setIsLoading(true);
    setError('');
  
    try {
      // First check if email is available
      const response = await fetch(`${API_BASE_URL}/auth/check-email?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.message || 'This email is already registered');
        setIsLoading(false);
        return;
      }
  
      const captchaToken = await handleReCaptchaVerify();
      await registerUser(email, password, captchaToken);
      setStep('verify');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to register';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const PasswordStrengthIndicator = ({ password }) => {
    const requirements = [
      { label: 'At least 8 characters', valid: password.length >= 8 },
      { label: 'At least one uppercase letter', valid: /[A-Z]/.test(password) },
      { label: 'At least one lowercase letter', valid: /[a-z]/.test(password) },
      { label: 'At least one number', valid: /\d/.test(password) },
      { label: 'At least one special character (@$!%*?&)', valid: /[@$!%*?&]/.test(password) }
    ];
  
    return (
      <div className="mt-2">
        <p className="text-sm font-medium text-gray-700 mb-1">Password requirements:</p>
        <ul className="text-sm space-y-1">
          {requirements.map((requirement, index) => (
            <li 
              key={index}
              className={`flex items-center ${
                requirement.valid ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              {requirement.valid ? '✓' : '○'} {requirement.label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Check email availability again before verifying
      const checkResponse = await fetch(`${API_BASE_URL}/auth/check-email?email=${encodeURIComponent(email)}`);
      const checkData = await checkResponse.json();
      
      if (!checkResponse.ok) {
        setError('This email was registered by someone else. Please try a different email.');
        setStep('register');
        setIsLoading(false);
        return;
      }

      const data = await verifyEmail(email, verificationCode, password);
      setAuthToken(data.accessToken);
      setUser(email);
      await refreshOrganization();
      navigate('/organization');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to verify email';
      
      if (errorMessage.includes('already registered') || errorMessage.includes('already taken')) {
        setError('This email was registered by someone else. Please try a different email.');
        setStep('register');
      } else if (errorMessage.includes('expired')) {
        setError('Verification code has expired. Please register again.');
        setStep('register');
      } else if (errorMessage.includes('invalid')) {
        setError('Invalid verification code. Please try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resendVerification(email);
      setError('New verification code sent');
    } catch (error) {
      setError('Failed to resend verification code');
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{step === 'register' ? 'Register' : 'Verify Email'}</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {step === 'register' ? (
            <form onSubmit={handleRegister}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    Email address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium">
                    Password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Password must contain at least 8 characters, including uppercase, lowercase, 
                    number, and special character
                  </p>
                  <PasswordStrengthIndicator password={password} />
                </div>

                <div className="space-y-2">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="h-4 w-4 rounded mt-1 cursor-pointer"
                    />
                    <label
                      htmlFor="terms"
                      className="ml-2 text-sm text-gray-600 cursor-pointer"
                    >
                      I accept the <Link to="/terms" className="text-primary hover:underline" target="_blank">Terms of Service</Link> and AI Usage Policy
                    </label>
                  </div>
                  
                  <div className="text-xs text-gray-500 pl-6 space-y-2 bg-gray-50 p-3 rounded-md">
                    <p>By accepting, you acknowledge that:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>You will comply with all applicable US/EU legislation regarding AI phone communications.</li>
                      <li>The AI Phone Agent service is for legitimate business purposes only and not for pranks or harassment.</li>
                      <li>You have the authority to use the phone numbers you provide to the service.</li>
                      <li>The AI may occasionally produce inaccurate information or "hallucinations" that don't reflect reality.</li>
                      <li>You understand the limitations of AI in understanding complex situations and will review any automated interactions.</li>
                      <li>You are responsible for ensuring compliance with local call recording and disclosure laws.</li>
                      <li>You will inform callers they are speaking with an AI system when required by law.</li>
                    </ul>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Registering...' : 'Register'}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerification}>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Please enter the verification code sent to {email}
                </p>
                
                <div>
                  <label htmlFor="code" className="block text-sm font-medium">
                    Verification Code
                  </label>
                  <Input
                    id="code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify Email'}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleResendCode}
                >
                  Resend Code
                </Button>
              </div>
            </form>
          )}
        </CardContent>
        <CardFooter className="text-center">
          Already have an account?{' '}
          <a href="/login" className="text-primary hover:underline">
            Login
          </a>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Register;